/**
 * Underwriting desk (M3 PR 10). Tex's working analysis of a submitted application: an
 * eight-section memo (PRD §6), evidenced facts, visible-until-resolved flags, a status that is
 * never "approved", and a freeze that makes the version immutable. Information requests are
 * the missing-information round trip with the borrower or dealer.
 *
 * Everything here is Tex-internal except `InformationRequestView`, which is projected to the
 * audience it is addressed to (never to the other one).
 */
import type { CaseStage, MoneyView, TexRole } from "./api-types";
import type { DiscrepancyView } from "./application";
import type { EvidenceLevel } from "./program-rules";

/** PRD §6, in order. Keys are stable identifiers; labels live in the UI. */
export const MEMO_SECTIONS = [
  "request_and_purpose",
  "business_profile",
  "repayment_source",
  "existing_obligations",
  "equipment_and_collateral",
  "verification_and_fraud",
  "proposed_partner",
  "conclusion",
] as const;
export type MemoSectionKey = (typeof MEMO_SECTIONS)[number];

export interface MemoSectionView {
  body: string;
  updated_at: string | null;
  updated_by_user_id: string | null;
}
export type MemoView = Record<MemoSectionKey, MemoSectionView>;

/** PRD §6: none of these is `credit_approved`. `not_proceeding` is Tex declining to package, not a credit denial. */
export const REVIEW_STATUSES = [
  "draft",
  "reviewed",
  "needs_information",
  "specialist_review",
  "ready_to_package",
  "not_proceeding",
] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/** Qualitative, with reasons — never a probability of default. */
export const REVIEW_CONFIDENCE = ["low", "medium", "high"] as const;
export type ReviewConfidence = (typeof REVIEW_CONFIDENCE)[number];

/** A fact the memo relies on, with where it came from and who checked it. */
export interface ReviewedFactView {
  id: string;
  field: string;
  value: string | number | boolean | MoneyView | null;
  evidence: EvidenceLevel;
  /** Source document/field or a short description of where the value was read from. */
  source: string;
  /** Date the source speaks as of (ISO date), not the date it was entered. */
  as_of: string | null;
  verified_by_user_id: string | null;
  verified_at: string | null;
  note: string | null;
}

export const FLAG_SEVERITIES = ["info", "warning", "blocking"] as const;
export type FlagSeverity = (typeof FLAG_SEVERITIES)[number];

/**
 * A contradiction, concern or discrepancy. Stays visible until resolved with a reason; a
 * `blocking` flag that is unresolved prevents `ready_to_package`. Discrepancy flags are
 * created by the system from the submitted revision and cannot be deleted, only resolved.
 */
export interface ReviewFlagView {
  id: string;
  code: string;
  severity: FlagSeverity;
  note: string;
  source: "system" | "reviewer";
  raised_by_user_id: string | null;
  raised_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution: string | null;
}

export interface ReviewView {
  id: string;
  case_id: string;
  revision_id: string;
  revision_number: number;
  version: number;
  status: ReviewStatus;
  reviewer_user_id: string;
  memo: MemoView;
  facts: ReviewedFactView[];
  flags: ReviewFlagView[];
  overall_evidence: EvidenceLevel | null;
  confidence: ReviewConfidence | null;
  confidence_reasons: string | null;
  target_program_version_id: string | null;
  /** Other programs this review authorizes packaging for; each may receive its own package and submission. */
  alternate_program_version_ids: string[];
  internal_notes: string | null;
  /** Optimistic-concurrency token for edits; distinct from `version`, which numbers frozen generations. */
  lock_version: number;
  frozen_at: string | null;
  frozen_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** What still stands between this review and `ready_to_package`; empty when nothing does. */
  readiness_blockers: ReadinessBlocker[];
}

export type ReadinessBlocker =
  | { kind: "memo_section_empty"; section: MemoSectionKey }
  | { kind: "unresolved_blocking_flag"; flag_id: string; code: string }
  | { kind: "open_information_request"; request_id: string; title: string }
  | { kind: "no_target_program" }
  | { kind: "target_program_retired"; program_version_id: string }
  | { kind: "confidence_missing" };

export interface ReviewVersionSummary {
  id: string;
  version: number;
  status: ReviewStatus;
  reviewer_user_id: string;
  frozen_at: string | null;
  created_at: string;
}

/** GET /cases/:id/review */
export interface ReviewResponse {
  /** The current (latest) version; null when no review has been opened. */
  review: ReviewView | null;
  versions: ReviewVersionSummary[];
  /** Frozen at submit; every kind here has a matching system flag on the review. */
  discrepancies: DiscrepancyView[];
}

// ── information requests ───────────────────────────────────────────────────

export const INFORMATION_REQUEST_AUDIENCES = ["borrower", "dealer"] as const;
export type InformationRequestAudience =
  (typeof INFORMATION_REQUEST_AUDIENCES)[number];

export const INFORMATION_REQUEST_KINDS = ["document", "clarification"] as const;
export type InformationRequestKind = (typeof INFORMATION_REQUEST_KINDS)[number];

/**
 * open → answered (by the addressee) → accepted | reopened(open) by Tex; cancelled by Tex at any point.
 * `open` and `answered` both count as outstanding for readiness.
 */
export const INFORMATION_REQUEST_STATUSES = [
  "open",
  "answered",
  "accepted",
  "cancelled",
] as const;
export type InformationRequestStatus =
  (typeof INFORMATION_REQUEST_STATUSES)[number];

export interface InformationRequestView {
  id: string;
  case_id: string;
  audience: InformationRequestAudience;
  kind: InformationRequestKind;
  /** For `document` requests: which document type would satisfy it. */
  document_type: string | null;
  title: string;
  detail: string;
  status: InformationRequestStatus;
  due_on: string | null;
  requested_at: string;
  response_note: string | null;
  response_document_id: string | null;
  responded_at: string | null;
  closed_at: string | null;
  /** Tex's note when accepting/cancelling/reopening; shown to the addressee. */
  close_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface InformationRequestListResponse {
  requests: InformationRequestView[];
}

// ── work queue ─────────────────────────────────────────────────────────────

export const NEXT_ACTIONS = [
  "start_review",
  "read_response",
  "write_memo",
  "resolve_flags",
  "awaiting_information",
  "specialist_review",
  "ready_to_freeze",
  "frozen_awaiting_package",
  "not_proceeding",
] as const;
export type NextAction = (typeof NEXT_ACTIONS)[number];

export interface WorkQueueItem {
  case_id: string;
  reference: string;
  stage: CaseStage;
  stage_changed_at: string;
  buyer_business_name: string;
  dealer_id: string;
  dealer_legal_name: string;
  requested_principal: MoneyView;
  specialist_review_required: boolean;
  assigned_reviewer: {
    user_id: string;
    name: string | null;
    email: string;
    tex_role: TexRole | null;
  } | null;
  review: {
    id: string;
    version: number;
    status: ReviewStatus;
    frozen_at: string | null;
  } | null;
  open_requests: number;
  answered_requests: number;
  unresolved_blocking_flags: number;
  next_action: NextAction;
  submitted_at: string | null;
}

export type WorkQueueScope = "mine" | "unassigned" | "all";

export interface WorkQueueResponse {
  items: WorkQueueItem[];
  reviewers: {
    user_id: string;
    name: string | null;
    email: string;
    tex_role: TexRole;
  }[];
}
